import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';
import { t } from 'i18next';

import { apiFactory, apiReturnResults, type ResultFail } from '@app/client';
import {
    type BaseParams,
    type PostParams,
    type ResourceId,
    type YatPageCollectionUpdate,
    type YatPageItemCreate,
    type YatPageItemPositionPayload,
    type YatPageItemResponse,
    type YatPageItemsResponse,
    type YatPageItemUpdate,
} from '@app/client/common/structs';
import { type ErrorResource } from '@app/client/common/structs/requests/error';
import { YatPageItems, YatPageV2 } from '@app/client/resources/yat-pages';
import { addNotification } from '@app/store/actions/notification';
import { type PageAssets } from '@app/store/slices/yat-page';

import { type AppThunk } from '../deferred-redux-store';
import { type RootState } from '../reducers';
import {
    addAlert,
    addYatPageBulkItems,
    addYatPageItem,
    publishYatPage,
    removeModifiedStatus,
    removeYatPageBulkItems,
    removeYatPageItem,
    removeYatPageModifiedItem,
    replaceAssetTempId,
    setYatPageDragging,
    setYatPageEditingItem,
    updateYatPageEditStates,
    updateYatPageItem,
    updateYatPageItemsOrder,
    type YatPageState,
} from '../slices';
import { type ContentItem, ContentType, type LinkContent, type NFTContent } from '../types/yat-page-actions';

// TODO: The goal is to try and remove this file and migrate / merge with `slices/yat-page.ts`.

let publishTimeout;

export function moveCardPosition(targetId: ContentItem['id']) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        publishTimeout && clearTimeout(publishTimeout);
        dispatch(updateYatPageItemsOrder(targetId));
    };
}

export function setDraggingItem(draggingId?: YatPageState['draggingId']) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch(setYatPageDragging(draggingId));
    };
}

// TODO define actual default title
export const DEFAULT_TITLE = t('grid_items:default_title');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNft = (item: any): item is ContentItem<NFTContent> => item.type === ContentType.NFT;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isLink = (item: any): item is ContentItem<LinkContent> => item.type === ContentType.LINK;

const getErrorAlert = (response: ResultFail<ErrorResource>, fallbackMessage: string) => {
    return typeof response.error === 'string' ? response.error : fallbackMessage;
};
const dispatchErrorAlert = (
    dispatch: ThunkDispatch<RootState, void, Action>,
    response: ResultFail<ErrorResource>,
    fallbackMessage: string,
) => {
    const message = getErrorAlert(response, fallbackMessage);
    dispatch(addAlert({ message }));
};

function getPageItemCreatePayload(pageItem: ContentItem): YatPageItemCreate {
    if (isNft(pageItem)) {
        const content = pageItem.content;
        if (!content) {
            throw new Error('NFT item has no content');
        }

        const {
            collection_name,
            collection_wiki_url,
            // contract_address,
            description,
            image,
            mime_type,
            minter,
            // owner_address,
            title,
            token_id,
            // token_uri,
            video,
        } = content;

        return {
            item_type: {
                Nft: {
                    // TODO: check if the below commentted out keys are needed on the backend?
                    // contract_address,
                    // image,
                    // mime_type,
                    // owner_address,
                    // token_id,
                    // token_uri,
                    description,
                    title,
                    type: 'nft',
                    page_preview: {
                        collection_name,
                        collection_wiki_url,
                        description,
                        image,
                        mime_type,
                        title,
                        token_id,
                        video,
                        image_alt: title,
                        minter: minter || false,
                        site_name: title,
                    },
                },
            },
        };
    }

    if (isLink(pageItem)) {
        const content = pageItem.content;
        if (!content) {
            throw new Error('NFT item has no content');
        }

        const { collapsed } = pageItem;
        const { description, image, title, url } = content;

        return {
            item_type: {
                Link: {
                    description,
                    url,
                    collapsed,
                    title: title || DEFAULT_TITLE,
                    type: 'url',
                    page_preview: {
                        image: image || null,
                        description: description || null,
                        image_alt: title || null,
                        video: null,
                        domain: null,
                        title: title || null,
                        site_name: title || null,
                    },
                },
            },
        };
    }

    throw new Error(`pageItem is not a supported type : ${pageItem.type}`);
}

function getPageItemUpdatePayload(pageItem: ContentItem): YatPageItemUpdate | undefined {
    if (!pageItem.content) return;

    if (isNft(pageItem)) {
        const { collection_name, image, title } = pageItem.content;
        return {
            id: pageItem.id,
            item_type: {
                Nft: {
                    title,
                    description: collection_name,
                    image: image,
                },
            },
        };
    }

    if (isLink(pageItem)) {
        const { description, image, title, url } = pageItem.content;
        return {
            id: pageItem.id,
            item_type: {
                Link: {
                    description: description,
                    image: image || null,
                    collapsed: pageItem.collapsed,
                    title: title || DEFAULT_TITLE,
                    url: url,
                },
            },
        };
    }
}

async function updateYatPageCollectionItems(
    pageId: string,
    items: {
        newItems: YatPageItemPositionPayload[];
        updatedItems: YatPageItemPositionPayload[];
        removedItems?: string[];
        collection?: string;
    },
    publish = false,
) {
    return await apiFactory().call(YatPageItems.updateYatPageCollectionItems, {
        pageId,
        value: {
            add: items.newItems,
            collection_id: items.collection,
            update: items.updatedItems,
            remove: items.removedItems,
            publish,
        },
    });
}

function getPageItemPositionPayload(pageItem, position): YatPageItemPositionPayload {
    return {
        item_id: pageItem.id,
        order: position,
    };
}

/**
 * ********************************
 * ***** PUBLISH FUNCTIONS ********
 * ********************************
 */

function getMovedPageItems(pageAssets: PageAssets) {
    const { draft, modified } = pageAssets;
    return modified
        .filter(
            (pageAssetId) =>
                draft[pageAssetId] &&
                ((draft[pageAssetId].asset.type === ContentType.LINK && draft[pageAssetId].asset.hasPersisted) ||
                    draft[pageAssetId].asset.type !== ContentType.LINK),
        )
        .map((pageAssetId) => getPageItemPositionPayload(draft[pageAssetId].asset, draft[pageAssetId].index));
}

function getEditedPageItems(pageAssets: PageAssets) {
    const { draft, modified } = pageAssets;
    return modified.filter(
        (pageAssetId) =>
            draft[pageAssetId] &&
            (draft[pageAssetId].asset.type === ContentType.LINK || draft[pageAssetId].asset.type === ContentType.YAT),
    );
}

/**
 *
 * Save PAGE ITEM changes (as draft)
 */
export function saveModifiedPageItems() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const { id: pageId } = getState().yatPage;
        const pageAssets = getState().yatPage.pageAssets;
        const { draft, modified } = pageAssets;

        // Update page items order
        if (pageId && modified.length > 0) {
            const movedPageItems = getMovedPageItems(pageAssets);
            await updateYatPageCollectionItems(pageId, {
                newItems: [],
                updatedItems: movedPageItems,
            }).then((response) => {
                if (!response.ok) {
                    dispatchErrorAlert(dispatch, response, 'Publish page items changes error');
                }
            });
        }

        // TODO: Update each asset's content
        const modifiedContentItems = getEditedPageItems(pageAssets);
        for (const pageAssetId of modifiedContentItems) {
            const item = getPageItemUpdatePayload(draft[pageAssetId].asset) as YatPageItemUpdate;
            await apiFactory()
                .call(YatPageItems.updateYatPageItem, {
                    value: {
                        ...item,
                    },
                })
                .then((response) => {
                    if (response.ok) {
                        dispatch(removeYatPageModifiedItem(item.id));
                    } else {
                        dispatchErrorAlert(dispatch, response, `updating item error ${pageAssetId}`);
                    }
                });
        }
    };
}

/**
 *
 * Delete PAGE ITEM (as draft)
 */
export function deletePageItem(pageAssetId: ResourceId, publishPage?: boolean) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const { id: pageId, pageAssets } = getState().yatPage;

        if (pageId) {
            const movedPageItems = getMovedPageItems(pageAssets);

            // Delete item and update moved items
            await updateYatPageCollectionItems(
                pageId,
                {
                    newItems: [],
                    updatedItems: movedPageItems,
                    removedItems: [pageAssetId],
                },
                publishPage,
            ).then((response) => {
                if (!response.ok) {
                    dispatchErrorAlert(dispatch, response, `deleting item error: ${pageAssetId}`);
                }
            });
        }

        // Save items that were moved
        if (!publishPage) {
            await dispatch(saveModifiedPageItems());
        }
    };
}

/**
 *
 * Add PAGE ITEM (as draft)
 */
export function addPageItem(pageAsset: ContentItem) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const { id: pageId, pageAssets } = getState().yatPage;

        if (pageId) {
            const newPageItem = getPageItemPositionPayload(pageAsset, 0);
            const movedPageItems = getMovedPageItems(pageAssets);

            if (pageAsset.type === ContentType.LINK) {
                const assetTempId = pageAsset.id;
                const cardPayload = getPageItemCreatePayload(pageAsset);
                const newCard = await apiFactory().call(YatPageItems.addItem, {
                    value: cardPayload,
                });
                if (pageId && newCard && newCard.ok && newCard.value && newCard.value.id) {
                    dispatch(replaceAssetTempId({ tempId: assetTempId, id: newCard.value.id }));
                    newPageItem.item_id = newCard.value.id;
                }
            }

            // Add item and update moved items
            await updateYatPageCollectionItems(pageId, {
                newItems: [newPageItem],
                updatedItems: movedPageItems,
                removedItems: [],
            }).then((response) => {
                if (!response.ok) {
                    dispatchErrorAlert(dispatch, response, `add item error: ${pageAsset.id}`);
                }
            });
        }
    };
}

export function addBulkPageItems(pageAssets: ContentItem[]) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        for (const newAsset of pageAssets) {
            dispatch(addPageItem(newAsset));
        }
    };
}

export function removeBulkPageItems(assetIds: ResourceId[]) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        for (const id of assetIds) {
            dispatch(deletePageItem(id));
        }
    };
}

/**
 *
 * Update PAGE ITEM (as draft)
 */
export function updatePageItem(pageAsset: ContentItem) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const { id: pageId } = getState().yatPage;

        if (pageId && pageAsset.type === ContentType.LINK) {
            if (pageAsset.isURLInvalid === false && !pageAsset.hasPersisted) {
                dispatch(addPageItem(pageAsset));
            }
        }
    };
}

/**
 *
 * Publish liquid id changes
 */
export const publishLiquidIdChanges = ({ publish = false }): AppThunk => {
    return async function (dispatch, getState) {
        const { id: pageId, pageItemsPersisted, liquidIDItems, liquid_id } = getState().yatPage;
        const repositionedLiquidIdItems: YatPageItemPositionPayload[] = [];
        const liquidIdDeletedItems: ResourceId[] = [];
        const newLiquidIdItems: YatPageItemPositionPayload[] = [];

        let liquidIdIndex = 0;
        for (const liquidIdItem of liquidIDItems) {
            const persistedItem = pageItemsPersisted.liquidIdItems.find((item) => item && item.id == liquidIdItem?.id);

            if (liquidIdItem && liquidIdItem.id && !persistedItem) {
                newLiquidIdItems.push(getPageItemPositionPayload(liquidIdItem, liquidIdIndex));
            } else {
                if (liquidIdIndex !== persistedItem?.draftOrder && liquidIdItem?.id) {
                    repositionedLiquidIdItems.push(getPageItemPositionPayload(persistedItem, liquidIdIndex));
                }
            }
            liquidIdIndex++;
        }

        pageItemsPersisted.liquidIdItems.forEach((persistedLiquidIdItem) => {
            const currentItem = liquidIDItems.find((item) => item && item.id == persistedLiquidIdItem?.id);
            if (!currentItem && persistedLiquidIdItem) {
                liquidIdDeletedItems.push(persistedLiquidIdItem.id);
            }
        });

        if (pageId && liquid_id) {
            // Update liquidID

            // Before adding new items, update and remove existing ones
            const updateAndDeleteResponse = await updateYatPageCollectionItems(
                pageId,
                {
                    newItems: [],
                    updatedItems: repositionedLiquidIdItems,
                    collection: liquid_id,
                    removedItems: liquidIdDeletedItems,
                },
                publish,
            ).then((response) => {
                if (!response.ok) {
                    dispatchErrorAlert(dispatch, response, 'Publishing Liquid Id change error');
                }
                return response;
            });

            if (updateAndDeleteResponse.ok) {
                await updateYatPageCollectionItems(
                    pageId,
                    {
                        newItems: newLiquidIdItems,
                        updatedItems: [],
                        collection: liquid_id,
                        removedItems: [],
                    },
                    publish,
                ).then((response) => {
                    if (!response.ok) {
                        dispatchErrorAlert(dispatch, response, 'Publishing Liquid Id change error');
                    }
                    return response;
                });
            }
        }
    };
};

/**
 *
 * Publish Bio changes
 */
export const updateBioChanges = (args?: { yat?: string; newUsername?: string; newBio?: string }): AppThunk => {
    return async function (dispatch, getState) {
        const { bio: subtitle, heading: title, eid } = getState().yatPage;
        const { yat, newUsername, newBio } = args || {};
        const yatString = yat || eid;
        const newTitle = newUsername || title;
        const newSubtitle = newBio || subtitle;

        if (yatString == null) {
            throw new Error('Publishing bio eid is null');
        }
        const value = {
            eid: yatString,
            bio: newSubtitle != null ? newSubtitle : undefined,
            heading: newTitle != null ? newTitle : undefined,
        };

        return await apiFactory()
            .call(YatPageV2.updateYatPage, {
                value: {
                    ...value,
                },
            })
            .then((response) => {
                if ((newUsername || newBio) && response.ok) {
                    postPublishPage(response.value.id);
                }
                if (!response.ok) {
                    dispatchErrorAlert(dispatch, response, 'Publishing bio changes error');
                }
                return response;
            });
    };
};

const postPublishPage = async (pageId: string | null, dispatch?: ThunkDispatch<RootState, void, Action>) => {
    if (pageId == null) {
        dispatch?.(addNotification({ message: `Invalid page id: ${pageId}`, severity: 'alert' }));
        return;
    }

    return await apiFactory().call(YatPageItems.updateYatPageCollectionItems, {
        pageId,
        value: {
            publish: true,
        },
    });
};

/**
 *
 * Publish handler
 */
export const publishChanges = (): AppThunk => {
    return async function (dispatch, getState) {
        const { id: pageId } = getState().yatPage;
        await dispatch(saveModifiedPageItems);
        await postPublishPage(pageId, dispatch);
        dispatch(publishYatPage());

        publishTimeout = setTimeout(() => {
            dispatch(removeModifiedStatus());
        }, 2 * 1000);
    };
};

/**
 * ********************************
 * ********************************
 */

export function addItem(contentItem: ContentItem) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        publishTimeout && clearTimeout(publishTimeout);
        dispatch(addYatPageItem(contentItem));
    };
}

export function addItemsBulk(items: ContentItem[]) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        publishTimeout && clearTimeout(publishTimeout);
        dispatch(addYatPageBulkItems(items));
    };
}

export function removeItem(itemId: ContentItem['id']) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        publishTimeout && clearTimeout(publishTimeout);
        dispatch(removeYatPageItem(itemId));
        dispatch(setEditingItemId());
    };
}

export function removeItemsBulk(itemIds: ContentItem['id'][]) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        publishTimeout && clearTimeout(publishTimeout);
        dispatch(removeYatPageBulkItems(itemIds));
    };
}

export function editItem(item: ContentItem) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        publishTimeout && clearTimeout(publishTimeout);
        dispatch(updateYatPageItem(item));
    };
}

export function setEditingItemId(id?: ContentItem['id']) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        publishTimeout && clearTimeout(publishTimeout);
        dispatch(setYatPageEditingItem(id));
        dispatch(updateYatPageEditStates({ editingItem: Boolean(id) }));
    };
}

export async function deletePortfolioLink(pageAssetId: ResourceId, pageId: string) {
    if (pageId && pageAssetId) {
        return await apiFactory().call(YatPageItems.updateYatPageCollectionItems, {
            pageId,
            value: {
                add: [],
                update: [],
                remove: [pageAssetId],
                publish: true,
            },
        });
    }
}

type UpdatePortfolioItems = BaseParams & PostParams<YatPageCollectionUpdate> & { pageId: ResourceId };
type UpdatePortfolioItem = BaseParams & PostParams<YatPageItemUpdate>;
type AddPortfolioItem = BaseParams & PostParams<YatPageItemCreate> & { pageId: string };

const updatePortfolioItems = (params: UpdatePortfolioItems) =>
    apiReturnResults<YatPageCollectionUpdate>(YatPageItems.updateYatPageCollectionItems, params);
const updatePortfolioItem = (params: UpdatePortfolioItem) =>
    apiReturnResults<YatPageItemsResponse>(YatPageItems.updateYatPageItem, params);
const addPortfolioItem = (params: AddPortfolioItem) =>
    apiReturnResults<YatPageItemResponse>(YatPageItems.addItem, params);

export async function updatePortfolioLink(itemData: YatPageItemUpdate, pageId: string) {
    if (pageId && itemData) {
        return updatePortfolioItem({
            value: {
                ...itemData,
            },
        }).then((r) => {
            if (r?.ok && r.value) {
                updatePortfolioItems({
                    pageId,
                    value: {
                        update: [{ item_id: itemData.id }],
                        publish: true,
                    },
                });
            }
            return r;
        });
    }
}

export async function addPortfolioLink(itemData: YatPageItemCreate, pageId: string) {
    if (pageId && itemData) {
        return addPortfolioItem({
            pageId,
            value: {
                ...itemData,
            },
        }).then((r) => {
            if (r?.ok) {
                updatePortfolioItems({
                    pageId,
                    value: {
                        add: [{ item_id: r.value.id, order: 0 }],
                        publish: true,
                    },
                });
            }
            return r;
        });
    }
}
